import React, { useEffect } from 'react';
import { Card, Tooltip } from '@mui/material';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { PAGE_TEXT, studentVisaInformation } from '../lang';
import {
  AssessmentAccordion,
  AssessmentGrid as RowDisplay,
  type RowData,
} from '../../../../../Assessment';
import {
  Alert,
  type AlertDetails,
  IconButton,
  Loading,
} from '../../../../../../components';
import { useGetVisaApplication, useGetNeedVisa } from '../../../../graphql';
import { SuccessPage } from '../../../../../../components/SuccessPage/SuccessPage';
import {
  VISA_STATUS_TEXT,
  VISA_STATUS_HELPER_TEXT,
} from '../../../../../AcademicRegistry/lang';
import { Help } from '@mui/icons-material';

export const VisaApplication: React.FunctionComponent = () => {
  const {
    VISA_APPLICATION_STATUS,
    VISA_APPLICATION_REF_NO,
    VISA_APPLICATION_UPDATE_DATE,
    VISA_APPLICATION_RESULT,
  } = PAGE_TEXT;

  const { applicationId } = useParams();

  const {
    needVisa,
    loading: needStudentVisaLoading,
    error: needVisaError,
  } = useGetNeedVisa(applicationId ?? '');

  const {
    data: visaApplication,
    loading: visaDataLoading,
    error: getVisaError,
    refetch,
  } = useGetVisaApplication(applicationId ?? '', needStudentVisaLoading);

  /**
   * update the data when the need visa status is loaded
   */
  useEffect(() => {
    if (!needStudentVisaLoading && needVisa) {
      refetch();
    }
  }, [needStudentVisaLoading]);

  const [openAlert, setOpenAlert] = React.useState(false);
  const [alertDetails] = React.useState<AlertDetails>({
    alertContent: '',
    alertType: 'success',
  });

  if (needVisaError ?? getVisaError) {
    return (
      <Alert
        openAlert={true}
        setOpenAlert={() => {}}
        alertDetails={{
          alertContent:
            `Failed to load visa status.` +
            (needVisaError as Error)?.message +
            (getVisaError as Error)?.message,
          alertType: 'error',
        }}
      />
    );
  }

  const visaStatuses: Record<any, string> = VISA_STATUS_TEXT;
  const visaStatusesHelperText: Record<any, string> = VISA_STATUS_HELPER_TEXT;

  const visaStatusDisplay: RowData[] = [
    {
      label: VISA_APPLICATION_REF_NO,
      value: visaApplication?.referenceNo ?? 'PENDING',
    },
    {
      label: VISA_APPLICATION_UPDATE_DATE,
      value:
        visaApplication?.applicationDate !== null &&
        visaApplication?.applicationDate !== undefined
          ? moment(visaApplication.applicationDate).format('DD/MM/YYYY')
          : 'Not Submitted',
    },
    {
      label: VISA_APPLICATION_RESULT,
      value: (
        <div className="flex items-baseline">
          <div className="mr-1">{visaStatuses[visaApplication?.status]}</div>
          <div>
            <Tooltip title={visaStatusesHelperText[visaApplication?.status]}>
              <span>
                <IconButton
                  icon={
                    <Help
                      sx={{
                        fontSize: '1rem',
                        color: '#951D1D',
                      }}
                    />
                  }
                />
              </span>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <Alert
        openAlert={openAlert}
        alertDetails={alertDetails}
        setOpenAlert={setOpenAlert}
      />
      {needStudentVisaLoading || visaDataLoading ? (
        <Loading />
      ) : (
        <>
          {needVisa ? (
            // needVisa
            <div>
              <AssessmentAccordion
                title="Notes to Applicants"
                AssessDetails={
                  <InformationContainer
                    className="p-4 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: studentVisaInformation }}
                  />
                }
                defaultExpanded
              />
              <AssessmentAccordion
                title={VISA_APPLICATION_STATUS}
                AssessDetails={
                  <Card
                    className="p-4 sleading-relaxed"
                    variant="outlined"
                    sx={{ boxShadow: 'lg' }}
                  >
                    <div className="ml-4 mr-4 mt-6 mb-6">
                      <RowDisplay rows={visaStatusDisplay} />
                    </div>
                  </Card>
                }
                defaultExpanded
              />
            </div>
          ) : (
            // no need visa
            <SuccessPageContainer>
              <SuccessPage
                icon="succeed"
                title="No Action Required"
                message="You do not need a student visa according to our system. Please contact Academic Registry if you have any enquires."
              />
            </SuccessPageContainer>
          )}
        </>
      )}
    </>
  );
};

const InformationContainer = styled.div`
  p {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;

    padding-left: 0.5rem;
    padding-right: 0.5rem;

    text-align: justify;
  }
`;

const SuccessPageContainer = styled.div`
  background: lightgray;
  border-radius: 16px;
  padding: 12px;
`;
